import { useTranslation } from 'react-i18next';
import Modal from 'react-bootstrap/Modal'
import { useState, useEffect, useContext, useRef } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { StyledAsyncSelect, StyledText, StyledInputText, ButtonAddGroup, StyledModalFooter } from '../styles'
import { Input } from 'reactstrap'
import { LabelInput, LabelNames } from '../../userRegister/style'
import { useToast } from '../../../context/Toast'
import { StyledModal } from '../modalUser/styles'
import debounce from 'debounce-promise'
import { getUsersByCompany, searchUsers } from '../../../services/user/user'
import { useAuth } from '../../../context/AuthContext'
import { Autocomplete, TextField } from '@mui/material'
import { Button } from '@mui/material'
import AutocompleteComponent from '../Autocomplete'
import { createGroup } from '../../../services/group/group'
import { User } from '../../../interfaces/user.interface'
import UserStatusLegend from '../components/UserStatusLegend'
import { getApiErrorMessage } from '../../../services/utils/apiError'
import { getInformedRequesterId } from '../../../services/utils/requester'
interface IFormState {
  name: string,
  users: {id: string, label: string, status?: number | null}[],
  responsible_users: {id: string, label: string, status?: number | null}[],
}

interface ModalAddGroupProps {
  show: boolean;
  userList: User[],
  onClose: () => void;
  onSuccess: (newGroup: any) => void;
  onError?: (error: any) => void;
}


function ModalAddGroup({ show, userList, onClose, onSuccess, onError }: ModalAddGroupProps) {
  const { t } = useTranslation('settings');
  const { showToast } = useToast();
  const { user } = useAuth();

  const [showLoading, setShowLoading] = useState(false);

  const [nameError, setNameError] = useState<string | null>(null);

  const [form, setForm] = useState<IFormState>({
    name: '',
    users: [],
    responsible_users: [],
  });

  const [userListOptions, setUserListOptions] = useState<{id: string, label: string, status?: number | null}[]>([]);

  const formatUserListInputOptions = (): {id: string, label: string, status?: number | null}[] => {
    let options: {id: string, label: string, status?: number | null}[] = [];

    userList.forEach((user: any) => {
      options.push({
        id: user.id,
        label: `${user?.first_name} ${user?.last_name} ( ${user?.email} )`,
        status: user?.status,
      });
    });

    return options;
  }

  useEffect(() => {
    if (!show) {
      setForm({
        name: '',
        users: [],
        responsible_users: [],
      });
      setNameError(null);
    }
  }, [show]);

  useEffect(() => {
    const loadAsyncOptions = async () => {
      // TODO: Implementar paginação de carregamento de usuários conectado com a rolagem na lista do AutocompleteComponent
      let loadedOptions = formatUserListInputOptions();

      setUserListOptions(loadedOptions);
    };

    loadAsyncOptions();
  }, [userList]);


  const onSubmit = async () => {
    const { name, users } = form;
    if (!name.trim()) {
      setNameError('O nome do grupo é obrigatório.');
      return;
    }
    setNameError(null);

    const company = user.company_visited?.id;
    const informedRequesterId = getInformedRequesterId(user?.user?.id);
    if (!company) {
      showToast(t('groups.sessionExpired'), 'info');
      return;
    }

    if (!informedRequesterId) {
      showToast(t('groups.requesterMissing'), 'error');
      return;
    }

    const groupDataToSend = {
      name: name,
      users: users.map(selectedUser => selectedUser.id),
      responsible_users: form.responsible_users.map(selectedUser => selectedUser.id),
      company_id: company,
      informedRequesterId,
    };

    try {
      setShowLoading(true);

      let result = await createGroup(groupDataToSend);

      onSuccess(result.groupCreated);

      setForm({
        name: '',
        users: [],
        responsible_users: [],
      });

      showToast(t('groups.created', { name: groupDataToSend.name }), 'success');
      onClose();

    } catch (error) {
      console.log(error);
      if (onError) {
        onError(error);
      } else {
        showToast(getApiErrorMessage(error, 'Falha ao cadastrar grupo'), 'error');
      }
    } finally {
      setShowLoading(false);
    }
  };


  // Caso a variável booelana "show" seja falsa o componente do modal não será renderizado
  if (show == false) {
    return null
  }

  return (
    <>
      <StyledModal show={show} onHide={onClose} centered size="lg" >
        <Modal.Header style={{ background: '#FFF' }}>
          <Modal.Title style={{ color: '#3C4043' }}>{t('groups.addTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#FFF', border: 'none' }}>
          <Row>
            <Col xs={12} sm={12}>
              <Form.Group className="mb-3 mt-3" controlId="name">
                <TextField
                  label={String(t('groups.name'))}
                  placeholder={String(t('groups.namePlaceholder'))}
                  value={form.name}
                  error={!!nameError}
                  helperText={nameError}
                  sx={{
                    width: '100%',
                    borderRadius: '0.625rem',
                    backgroundColor: '#F1F1F1',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.625rem',
                      '& input': {
                        backgroundColor: 'transparent',
                        fontSize: '16px',
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',
                    },
                  }}
                  onChange={(e: any) => {
                    setForm({
                      ...form,
                      ['name']: e.target.value
                    });
                    if (e.target.value.trim()) setNameError(null);
                  }}
                />
              </Form.Group>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.9rem', marginBottom: '0rem' }}>
                <UserStatusLegend />
              </div>
            </Col>
            <Col xs={12} sm={12}>
              <Form.Group className="mb-3 mt-2" controlId="users">
                <AutocompleteComponent
                  id="users-autocomplete"
                  options={userListOptions}
                  selectedOptions={form.users || []} // Garante que seja um array
                  showStatusIndicators
                  label={String(t('groups.members'))}
                  placeholder={String(t('groups.membersPlaceholder'))}
                  onChange={(newValue) => {
                    // Atualiza o estado com os itens selecionados
                    setForm({
                      ...form,
                      users: newValue.map((user: any) => ({
                        id: user.id,
                        label: user.label,
                        status: user.status,
                      })), // Garante que o formato seja consistente
                    });
                  }}
                  onRemove={(removedId) => {
                    // Remove o item pelo ID
                    setForm({
                      ...form,
                      users: form.users.filter((user: any) => user.id !== removedId),
                    });
                  }}
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={12}>
              <Form.Group className="mb-3 mt-2" controlId="responsible_users">
                <AutocompleteComponent
                  id="responsible-users-autocomplete"
                  options={userListOptions}
                  selectedOptions={form.responsible_users || []}
                  showStatusIndicators
          label={String(t('groups.responsible'))}
          placeholder={String(t('groups.responsiblePlaceholder'))}
                  onChange={(newValue) => {
                    // Atualiza o estado com os responsáveis selecionados
                    setForm({
                      ...form,
                      responsible_users: newValue.map((user: any) => ({
                        id: user.id,
                        label: user.label,
                        status: user.status,
                      })),
                    });
                  }}
                  onRemove={(removedId) => {
                    // Remove o responsável pelo ID
                    setForm({
                      ...form,
                      responsible_users: form.responsible_users.filter((user: any) => user.id !== removedId),
                    });
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

        </Modal.Body>
        <StyledModalFooter style={{ background: '#FFF' }}>
          <ButtonAddGroup >
            <Button
              sx={{
                textTransform: 'capitalize',
                padding: "0.5em 1.5em",
                borderRadius: '2em',
                fontSize: '1em',
                fontWeight: 500,
                border: '1px solid #a0a0a0',
              }}

              variant='text'
              onClick={onClose}
              disabled={showLoading}
            >
              {t('groups.close')}
            </Button>

            <Button
              variant='contained'
              sx={{
                textTransform: 'capitalize',
                background: '#2491FF',
                borderRadius: '2em',
                fontSize: '1em',
                fontWeight: 'semibold ',
              }}
              onClick={() => {
                onSubmit();
              }}
              disabled={showLoading}
            >
              {showLoading ? t('groups.confirming') : t('groups.confirm')}
            </Button>
          </ButtonAddGroup>
        </StyledModalFooter>
      </StyledModal>
    </>
  )
}

export default ModalAddGroup;
